import type { Directive } from 'vue'

const detachers = new WeakMap<HTMLElement, () => void>()

function hasFinePointer(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches
}

/** Writes the pointer's position over `el` to `--pointer-x` and `--pointer-y`, at most once per frame; does nothing without a fine pointer; returns the detach function. */
function attachPointerLight(el: HTMLElement): () => void {
  if (!hasFinePointer()) return () => {}

  let frame = 0
  let clientX = 0
  let clientY = 0

  function paint(): void {
    frame = 0
    const rect = el.getBoundingClientRect()
    el.style.setProperty('--pointer-x', `${clientX - rect.left}px`)
    el.style.setProperty('--pointer-y', `${clientY - rect.top}px`)
  }

  function onPointerMove(event: PointerEvent): void {
    clientX = event.clientX
    clientY = event.clientY
    if (frame === 0) frame = requestAnimationFrame(paint)
  }

  el.addEventListener('pointermove', onPointerMove, { passive: true })

  return () => {
    el.removeEventListener('pointermove', onPointerMove)
    if (frame !== 0) cancelAnimationFrame(frame)
    el.style.removeProperty('--pointer-x')
    el.style.removeProperty('--pointer-y')
  }
}

/** Lights a `pointer-glow` element where the pointer is: `v-pointer-light` on the element. */
export const vPointerLight: Directive<HTMLElement> = {
  mounted(el) {
    detachers.set(el, attachPointerLight(el))
  },
  beforeUnmount(el) {
    detachers.get(el)?.()
    detachers.delete(el)
  },
}
